// Экран задач-контролёров. Единственная настоящая проверка карты.
//
// Двадцать задач: десять по навыкам, которые система считает известными,
// десять — по неизвестным. Ученик не знает, какая задача из какой группы, и
// не видит ни одного вердикта: контролёры не должны влиять ни на состояние,
// ни на поведение. Показ «сходится» после каждой превратил бы замер в урок.
//
// Открывается по ссылке `?control=1`, не из набора дня. Это инструмент
// взрослого, а не часть ежедневного пути.

import { useEffect, useRef, useState } from "react";
import { api, useMutation, useQuery } from "../api";
import { reasonOf } from "../lib/errors";
import { Formula } from "../lib/Formula";

export function Control({ learnerKey }: { learnerKey: string }) {
  const state = useQuery(api.control.current, { learnerKey });
  const delayed = useQuery(api.control.currentDelayed, { learnerKey });
  const start = useMutation(api.control.start);
  const startDelayed = useMutation(api.control.startDelayed);
  const answer = useMutation(api.control.answer);
  const answerDelayed = useMutation(api.control.answerDelayed);

  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const starting = useRef(false);
  const startingDelayed = useRef(false);

  useEffect(() => {
    if (state === null && !starting.current) {
      starting.current = true;
      void start({ learnerKey }).catch((e: unknown) => setError(reasonOf(e)));
    }
  }, [state, learnerKey, start]);

  useEffect(() => {
    if (state?.question === null && delayed === null && !startingDelayed.current) {
      startingDelayed.current = true;
      void startDelayed({ learnerKey })
        .catch((e: unknown) => setError(reasonOf(e)))
        .finally(() => {
          startingDelayed.current = false;
        });
    }
  }, [state, delayed, learnerKey, startDelayed]);

  const baselineQuestion = state?.question ?? null;
  const question = baselineQuestion ?? delayed?.question ?? null;
  const delayedMode = baselineQuestion === null && delayed !== null;
  useEffect(() => setRaw(""), [question?.itemKey]);

  const submit = (): void => {
    if (busy || question === null) return;
    setBusy(true);
    const send = delayedMode ? answerDelayed : answer;
    void send({ learnerKey, itemKey: question.itemKey, raw })
      .then(() => setBusy(false))
      .catch((e: unknown) => {
        setBusy(false);
        setError(reasonOf(e));
      });
  };

  if (error !== null) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <div className="eyebrow">Контролёры</div>
          <h1 className="h1">Замер не запускается</h1>
          <div className="notice" style={{ marginTop: 12 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (state === undefined || state === null || delayed === undefined) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 200 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  if (question === null) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <div className="eyebrow">Контроль обучения</div>
          <h1 className="h1">Сейчас проверок нет</h1>
          <p className="lede">
            Исходный замер завершён: отвечено <span className="num">{state.answered}</span> из{" "}
            <span className="num">{state.total}</span>. Проверки переноса появятся
            здесь через 7 и 21 день после изучения навыков.
          </p>
          <div className="card card--dashed" style={{ marginTop: 16 }}>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              Доли верных по двум группам:{" "}
              <span className="num">npx convex run control:result</span>{" "}
              с ключом ученика.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cols cols--solo">
      <div className="col">
        <div>
          <div className="eyebrow">
            {delayedMode ? `Проверка через ${delayed?.windowDays} дней` : "Исходный замер"}
          </div>
          <h1 className="h1">
            {delayedMode ? (
              "Незнакомая задача"
            ) : (
              <>
                Задача <span className="num">{state.answered + 1}</span> из{" "}
                <span className="num">{state.total}</span>
              </>
            )}
          </h1>
          <p className="lede">
            Проверок здесь нет: ни вердикта, ни разбора. Не знаешь — оставь
            поле пустым и иди дальше, это не ошибка.
          </p>
        </div>

        <div className="card">
          <div className="card__label">Условие</div>
          <div className="statement">
            <Formula source={question.statement} chip />
          </div>
        </div>

        <div className="card">
          <div className="answer">
            <span className="answer__label">Ответ</span>
            <input
              className="field"
              value={raw}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder={question.inputHint}
              onChange={(event) => setRaw(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") submit();
              }}
            />
            <button className="btn btn--primary" disabled={busy} onClick={submit}>
              {busy ? "Записываю" : "Дальше"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
